import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import { settingsApi, type TenantSettings } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  NumberField,
  Panel,
  ReadOnlyField,
  SaveErrorNotice,
  useSaveErrors,
  useSaveFailureFocus,
  useServerForm,
  useToast,
} from '../../components/ui/index.js';

const fromServer = (source: TenantSettings) => ({
  paymentTermsDays: source.paymentTermsDays as number | null,
});

/**
 * Finance settings — Phase 11.
 *
 * `paymentTermsDays` is the laboratory's OWN policy, applied by the backend
 * only when a draft names no due date of its own (D152): issue date plus this
 * many days. The backend's default for a new laboratory is 30; this page
 * never restates it (CLAUDE.md section 6.1) and shows exactly what the server
 * reports.
 *
 * Saved on its own, apart from the main settings form, for the same reason
 * `ReportSettingsPanel` is: a change here should never ride along with an
 * unrelated edit to the tax rate.
 */
export function FinanceSettingsPanel({ settings }: { readonly settings: TenantSettings }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show } = useToast();
  const queryClient = useQueryClient();

  const canEdit = can(PERMISSIONS.SETTINGS_UPDATE);

  // Authoritative while edited, reset only by this panel's own success (D205).
  const draft = useServerForm(settings, fromServer, fromServer(settings));
  const paymentTermsDays = draft.values.paymentTermsDays;

  const save = useMutation({
    mutationFn: ({ days }: { days: number | null; mark: number }) =>
      settingsApi.update({ paymentTermsDays: days ?? settings.paymentTermsDays }),
    onSuccess: async (saved, { mark }) => {
      draft.saved(saved, mark);
      queryClient.setQueryData(['settings'], saved);
      show({ tone: 'success', message: t('settings.savedNotice') });
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
  const { fields: serverErrors } = useSaveErrors(save.error);
  const panelRef = useRef<HTMLDivElement>(null);
  const noticeRef = useRef<HTMLDivElement>(null);
  useSaveFailureFocus(save.error, panelRef, noticeRef);

  return (
    <Panel
      title={t('settings.finance.title')}
      description={t('settings.finance.description')}
    >
      <div ref={panelRef} className="flex flex-col gap-gutter">
        {canEdit ? (
          <NumberField
            label={t('settings.finance.paymentTermsDays')}
            description={t('settings.finance.paymentTermsDaysHint')}
            value={paymentTermsDays}
            onChange={(value) => draft.set('paymentTermsDays', value)}
            {...(serverErrors.paymentTermsDays ? { error: serverErrors.paymentTermsDays } : {})}
          />
        ) : (
          <ReadOnlyField label={t('settings.finance.paymentTermsDays')}>
            <span dir="ltr">{settings.paymentTermsDays}</span>
          </ReadOnlyField>
        )}

        {save.error ? (
          <div ref={noticeRef} tabIndex={-1} className="outline-none">
            <SaveErrorNotice error={save.error} title={t('settings.saveFailed')} />
          </div>
        ) : null}

        {canEdit ? (
          <ActionBar>
            <ActionButton
              priority="primary"
              isLoading={save.isPending}
              onPress={() => save.mutate({ days: paymentTermsDays, mark: draft.mark() })}
            >
              {t('settings.save')}
            </ActionButton>
          </ActionBar>
        ) : null}
      </div>
    </Panel>
  );
}
